import { useEffect, useState } from 'react';

/**
 * Live-Countdown bis zu einem ISO-Timestamp.
 *
 * - Tickt jede Sekunde (setInterval).
 * - Liefert "Xh Ym Zs" bzw. "Xm Ys" bzw. "Zs" je nach Restzeit.
 * - aria-live="polite" damit Screen-Reader bei wichtigen Schwellen
 *   (z.B. < 5min) updaten - aber nicht ueber-spammed werden.
 *
 * Bei Ablauf zeigt der Component `expiredLabel` (Default: "abgelaufen").
 *
 * A11y-Note: aria-live=polite ist die richtige Wahl - "assertive" würde
 * jede Sekunde unterbrechen. Wer sehr feingranulare Updates braucht, kann
 * `silent`=true setzen und stattdessen am Eltern-Element sr-only-Hinweise
 * setzen.
 */

interface LiveCountdownProps {
  /** ISO-8601 Timestamp, z.B. session.expires_at. */
  target: string;
  /** Custom Label bei Ablauf, Default "abgelaufen". */
  expiredLabel?: string;
  /** aria-live abschalten, falls woanders schon gemeldet wird. */
  silent?: boolean;
  className?: string;
  /**
   * Optional: Render-Override für die Restzeit. Bekommt
   * `{hours, minutes, seconds, expired}` und liefert ReactNode.
   */
  render?: (parts: CountdownParts) => string;
}

export interface CountdownParts {
  hours: number;
  minutes: number;
  seconds: number;
  /** Gesamtsekunden Rest (>= 0). */
  totalSeconds: number;
  expired: boolean;
}

export function LiveCountdown({
  target,
  expiredLabel = 'abgelaufen',
  silent = false,
  className,
  render,
}: LiveCountdownProps): JSX.Element {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const targetMs = new Date(target).getTime();
  const diff = Math.max(0, targetMs - now);
  const parts = toParts(diff);
  const text = parts.expired
    ? expiredLabel
    : (render?.(parts) ?? defaultRender(parts));

  return (
    <span
      className={className}
      aria-live={silent ? undefined : 'polite'}
      aria-atomic="true"
      data-testid="live-countdown"
    >
      {text}
    </span>
  );
}

export function toParts(diffMs: number): CountdownParts {
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { hours, minutes, seconds, totalSeconds, expired: totalSeconds === 0 };
}

function defaultRender(parts: CountdownParts): string {
  if (parts.hours > 0) return `${parts.hours}h ${pad(parts.minutes)}m`;
  if (parts.minutes > 0) return `${parts.minutes}m ${pad(parts.seconds)}s`;
  return `${parts.seconds}s`;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}
